import { ApiError, isOfflineError, isUpstreamDown } from '@/lib/api'
import { Button } from './Button'

interface Copy {
  title: string
  body: string
  tone: 'warn' | 'danger'
}

function describe(error: unknown): Copy {
  if (isOfflineError(error)) {
    return {
      title: 'You appear to be offline',
      body: 'Hermes could not be reached. Everything will reload once you are back online.',
      tone: 'warn',
    }
  }
  if (error instanceof ApiError && error.code === 'not_configured') {
    return {
      title: 'Not set up yet',
      body: 'This part of the portal has not been connected to the server.',
      tone: 'warn',
    }
  }
  if (isUpstreamDown(error)) {
    return {
      title: 'The home server is unreachable',
      body: 'This section will fill in on its own once the server is back. Nothing is lost.',
      tone: 'warn',
    }
  }
  const message = error instanceof ApiError ? error.message : 'Something went wrong.'
  return { title: 'Could not load this', body: message, tone: 'danger' }
}

/*
 * Amber for "expected and temporary", red for "actually broke". The two are not
 * interchangeable: an outage is a scheduled fact of this system and must not
 * carry the same lamp as a fault.
 */
const TONES: Record<Copy['tone'], { panel: string; label: string; lamp: string }> = {
  warn: { panel: 'border-warn/25 bg-warn-dim', label: 'text-warn', lamp: 'bg-warn' },
  danger: { panel: 'border-danger/30 bg-danger-dim', label: 'text-danger', lamp: 'bg-danger' },
}

/**
 * Inline, section-scoped failure copy. A downed integration must never take the
 * page with it, so this is always rendered *inside* a section, never instead of
 * the app shell.
 */
export function ErrorNotice({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown
  onRetry?: () => void
  compact?: boolean
}) {
  const { title, body, tone } = describe(error)
  const requestId = error instanceof ApiError ? error.requestId : undefined
  const palette = TONES[tone]

  if (compact) {
    return (
      <p role="status" className="flex items-baseline gap-2 text-sm text-muted">
        <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${palette.lamp}`} />
        <span>
          {title}. {body}
        </span>
      </p>
    )
  }

  return (
    <div role="status" className={`rounded border px-4 py-3 ${palette.panel}`}>
      <p className={`signage text-[10px] ${palette.label}`}>{title}</p>
      <p className="mt-1.5 max-w-prose text-sm text-muted">{body}</p>
      {/* The reference is the only thing an admin can act on, so it stays mono
          and selectable rather than being folded into the sentence above. */}
      {requestId ? (
        <p className="mt-1.5 font-mono text-[11px] text-faint">Reference {requestId}</p>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="secondary" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}
